import { Box, type Breakpoint } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion, useReducedMotion } from "framer-motion";
import { type CSSProperties, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ALPHA, GRID_RADIUS, INK, NEUTRAL, OUTLINE_SWITCH, RADIUS, STATUS_BADGE, SURFACE } from "../../../theme/tokens";
import { TOUCH_TARGET_HEIGHT_SX } from "../touchTarget";

/**
 * Corner radius per `shape`, in px because the sliding indicator is an inline `style` on a
 * `motion.div` — an inline style no `sx` can reach, which is exactly why the shape is a PROP and not
 * something a call site can override.
 *
 * `pill` is the long-standing 20px fully-rounded track. `rectangular` is the app's metadata-chip step
 * (`RADIUS.CARD` × `GRID_RADIUS.CHIP`, i.e. `theme.shape.borderRadius` × 0.75) resolved to px for the
 * same reason — for a surface whose other controls are rectangular chips.
 */
const TRACK_RADIUS_PX = {
	pill: 20,
	rectangular: RADIUS.CARD * GRID_RADIUS.CHIP,
} as const;

export type FlatSwitchOption<T> = {
	label: ReactNode;
	value: T;
	disabled?: boolean;
	/**
	 * Optional test id for the option button
	 */
	"data-testid"?: string;
};

type Props<T> = {
	rootWidth?: CSSProperties["width"] | Partial<Record<Breakpoint, CSSProperties["width"]>>;
	rootMaxWidth?: CSSProperties["width"] | Partial<Record<Breakpoint, CSSProperties["width"]>>;
	options: FlatSwitchOption<T>[];
	activeIndexOrValue: number | T;
	onSwitch: (option: FlatSwitchOption<T>, index: number) => void;
	/**
	 * Optional delay in milliseconds before calling the onSwitch callback after toggling.
	 * Pass 0 to disable this behavior and call onSwitch immediately on toggle.
	 */
	delayCallback?: number;
	/**
	 * Optional test id for the component
	 */
	"data-testid"?: string;
	/**
	 * Annie walkthrough anchor (`context/standards/frontend.md`). Explicit because this component doesn't
	 * spread rest props, and TypeScript exempts hyphenated JSX attributes from excess-property checking —
	 * so an unknown `data-*` prop is dropped silently rather than failing the build.
	 */
	"data-annie-target"?: string;
	fontSizeOverrides?: Partial<Record<Breakpoint, CSSProperties["fontSize"]>>;
	maxWidthOverrides?: Partial<Record<Breakpoint, CSSProperties["width"]>>;
	/**
	 * Per-option padding, merged over the default scale.
	 *
	 * The default is deliberately tight so a switch fits in a dense header. A long or multi-word label
	 * ("By person") sits too close to the pill's edge at that padding, especially against the 20px
	 * corner radius — override it rather than making every switch in the app wider.
	 */
	paddingOverrides?: Partial<Record<Breakpoint, CSSProperties["padding"]>>;
	/**
	 * Visual variant. "filled" (the default when omitted) is the classic gray pill with a white
	 * sliding indicator; "outline" is a transparent brand-outlined pill with a brand-tinted
	 * sliding indicator — for mode toggles that must stand out on a white header; "brand" fills the
	 * indicator with the primary color and inverts the active label to white.
	 *
	 * Reach for "brand" when the switch IS the decision on the screen rather than a view filter, and
	 * a white-on-neutral indicator is too quiet to register as a state change — the sign-in
	 * one-time-code/password toggle, where missing which mode you are in means typing the wrong
	 * secret. It follows the user's chosen primary at runtime (CSS variable), so it retints with the
	 * theme instead of pinning brand purple.
	 */
	variant?: "filled" | "outline" | "brand";
	/**
	 * Opt-in: give every option the SAME width (the widest label's) instead of sizing each to its
	 * own text. The sliding indicator then only translates and never resizes mid-flight, which
	 * reads as a true segmented control — worth it when labels differ a lot in length
	 * ("Merge" vs "Replace"). Omit for the default content-sized behavior.
	 */
	equalWidthOptions?: boolean;
	/**
	 * Ink of the ACTIVE segment's label (filled/outline variants). Defaults to `primary.dark`, i.e. the
	 * org's brand colour; pass a fixed accent on a surface whose design specifies one. Ignored by the
	 * `brand` variant, whose label has to invert to white over its solid fill.
	 */
	activeColor?: string;
	textWrap?: CSSProperties["textWrap"];
	/**
	 * Corner shape of the track, the option buttons and the sliding indicator — all three together,
	 * since rounding some and not the others reads as a bug.
	 *
	 * Additive: omitted means `pill`, today's fully-rounded control, so every existing call site is
	 * unchanged. Pass `rectangular` on a surface that has moved to rectangular chips.
	 */
	shape?: keyof typeof TRACK_RADIUS_PX;
	/**
	 * Opt-in: every option reaches the finger-sized `TOUCH_TARGET_MIN` height on coarse pointers (`TOUCH_TARGET_HEIGHT_SX`
	 * — height only, since the options share the row's width). For a switch on a phone screen; absent = today's density,
	 * so every existing caller is unchanged.
	 */
	touchTarget?: boolean;
};

/**
 * A flat switch component with N positions that can be toggled (one active at a time).
 * @param onSwitch
 * @param delayCallback
 * @param options
 * @param activeIndexOrValue - can be either the index of the active option or its value
 * @param dataTestId - optional test id for the component
 * @param fontSizeOverrides
 * @param maxWidthOverrides
 * @param rootWidth
 * @param rootMaxWidth
 * @constructor
 */
export const SystemFlatSwitch = <T,>({
	options,
	activeIndexOrValue,
	onSwitch,
	delayCallback = 200,
	"data-testid": dataTestId,
	"data-annie-target": annieTarget,
	fontSizeOverrides,
	maxWidthOverrides,
	paddingOverrides,
	rootWidth,
	rootMaxWidth,
	variant,
	equalWidthOptions,
	activeColor,
	textWrap,
	// DEFAULT: omitted → the pill the component has always been; only a caller on a rectangular
	// surface opts out, and it must opt out for the whole control at once.
	shape = "pill",
	touchTarget,
}: Props<T>) => {
	const trackRadius = TRACK_RADIUS_PX[shape];
	const isOutline = variant === "outline";
	const isBrand = variant === "brand";
	const getIndexFromActiveIndexOrValue = useCallback((): number => {
		if (typeof activeIndexOrValue === "number") {
			return activeIndexOrValue;
		}
		const foundIndex = options.findIndex((option) => option.value === activeIndexOrValue);
		return foundIndex !== -1 ? foundIndex : 0;
	}, [activeIndexOrValue, options]);

	const [active, setActive] = useState(getIndexFromActiveIndexOrValue);
	const isIndexActive = (index: number) => active === index;

	// The active indicator slides between options; honor reduced-motion by snapping it instead.
	const prefersReducedMotion = useReducedMotion();

	const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
	// `measured` gates the indicator until we know the active button's real
	// position, so it never paints at the placeholder {left:0,width:0}. The first
	// measure must snap (no animation) — only later switches/resizes slide.
	const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number; measured: boolean }>({ left: 0, width: 0, measured: false });

	const measureIndicator = useCallback(() => {
		const btn = buttonRefs.current[active];
		if (btn) {
			setIndicatorStyle({ left: btn.offsetLeft, width: btn.offsetWidth, measured: true });
		}
	}, [active]);

	// Measure before the browser paints so the indicator is correctly positioned on
	// the very first painted frame (no mount flash from the {0,0} placeholder).
	useLayoutEffect(() => {
		measureIndicator();
	}, [measureIndicator]);

	useEffect(() => {
		// Re-measure on resize (font size changes at breakpoints)
		const observer = new ResizeObserver(() => measureIndicator());
		const buttons = buttonRefs.current.filter(Boolean) as HTMLButtonElement[];
		for (const btn of buttons) observer.observe(btn);
		return () => observer.disconnect();
	}, [measureIndicator, options.length]);

	useEffect(() => {
		const activeIndex = getIndexFromActiveIndexOrValue();
		setActive((prevState) => {
			if (prevState !== activeIndex) {
				return activeIndex;
			}
			return prevState;
		});
	}, [getIndexFromActiveIndexOrValue]);

	const onClickSwitch = (option: FlatSwitchOption<T>, newlyActivatedIndex: number) => {
		if (!isIndexActive(newlyActivatedIndex) && !option.disabled) {
			setActive(newlyActivatedIndex);
			if (!delayCallback) {
				onSwitch(option, newlyActivatedIndex);
			} else {
				setTimeout(() => {
					onSwitch(option, newlyActivatedIndex);
				}, delayCallback);
			}
		}
	};

	return (
		<Box
			data-testid={dataTestId}
			data-annie-target={annieTarget}
			sx={{
				width: rootWidth ?? "fit-content",
				maxWidth: rootMaxWidth ?? "100%",
				overflow: "visible",
				position: "relative",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				// Filled track is a legible neutral trough (not the near-white NEUTRAL[150]) with a
				// hairline edge, so the whole control reads as one clickable segmented toggle on
				// both white and brand-tinted surfaces — the active white pill now clearly pops.
				backgroundColor: isOutline ? "transparent" : NEUTRAL[200],
				border: `1px solid ${isOutline ? OUTLINE_SWITCH.BORDER : NEUTRAL[300]}`,
				borderRadius: `${trackRadius}px`,
				padding: "2px",

				"& button": {
					position: "relative",
					zIndex: 1,
					borderRadius: `${trackRadius}px`,
					border: "none",
					cursor: "pointer",
					// `flex: 1 1 0` + zero basis makes every option share the container width equally, and
					// the container's own fit-content width resolves to (widest option × count) — so the
					// options match the widest label without the caller pinning a pixel width.
					...(equalWidthOptions ? { flex: "1 1 0", minWidth: 0 } : {}),
					padding: { xs: "4px 8px", xl: "4px 16px", ...paddingOverrides },
					fontSize: {
						xs: "10px",
						lg: "12px",
						xl: "14px",
						xxl: "14px",
						...fontSizeOverrides,
					},
					maxWidth: {
						xs: "none",
						xl: "none",
						xxl: "none",
						...maxWidthOverrides,
					},
					...(touchTarget ? TOUCH_TARGET_HEIGHT_SX : {}),
					wordBreak: "keep-all",
					textAlign: "center",
					whiteSpace: textWrap ?? "normal",
					fontWeight: 500,
					transition: "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
					backgroundColor: "transparent",
					color: INK.SECONDARY,
					outline: "none",
					// Inactive options get real hover feedback: a faint white "preview pill" (where
					// the active indicator would slide) plus darker ink — so they read as clickable
					// before being pressed. Gated to real pointers per the motion craft.
					"@media (hover: hover) and (pointer: fine)": {
						"&:not(.active):not(.disabled):hover": {
							backgroundColor: alpha(SURFACE, ALPHA.SEGMENT_HOVER_FILL),
							color: INK.PRIMARY,
						},
					},
					// Keyboard focus ring (brand-tinted) — a11y for tab navigation.
					"&:focus-visible": {
						boxShadow: `0 0 0 3px ${alpha(STATUS_BADGE.BRAND_FALLBACK_HEX, ALPHA.FOCUS_RING)}`,
					},
					"&.active": {
						fontWeight: 600,
						backgroundColor: "transparent",
						// The brand indicator is a solid fill, so the active label has to invert to stay
						// legible on it; the other variants sit on a light indicator and keep brand ink.
						color: isBrand ? INK.ON_DARK : (activeColor ?? "primary.dark"),
					},
					"&.disabled": {
						cursor: "not-allowed",
						color: INK.DISABLED,
					},
				},
			}}
		>
			{/* Render only once measured, with `initial={false}` so it mounts directly at
			    the active button's position — no spring from the {0,0} placeholder on
			    load. After mount, index changes animate via the spring (or jump, under
			    reduced motion). */}
			{indicatorStyle.measured && (
				<motion.div
					initial={false}
					animate={{ left: indicatorStyle.left, width: indicatorStyle.width }}
					transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 520, damping: 36 }}
					style={{
						position: "absolute",
						top: 2,
						bottom: 2,
						borderRadius: trackRadius,
						// BRAND_FALLBACK (the CSS variable) rather than the hex, so the pill retints with the
						// user's chosen primary. No alpha() here, which is what forbids the variable elsewhere.
						background: isBrand ? STATUS_BADGE.BRAND_FALLBACK : isOutline ? alpha(STATUS_BADGE.BRAND_FALLBACK_HEX, OUTLINE_SWITCH.ACTIVE_FILL_ALPHA) : SURFACE,
						// The filled variant's lift comes from a brand-tinted shadow, which can only be built
						// from the static hex (alpha() rejects CSS vars). Brand fills the indicator with the
						// RUNTIME primary, so that shadow would ring a retinted pill in purple — and a solid
						// fill needs no lift to read as active anyway.
						boxShadow: isOutline || isBrand ? "none" : `0px 2px 4px ${alpha(STATUS_BADGE.BRAND_FALLBACK_HEX, 0.15)}`,
					}}
				/>
			)}
			{options.map((option, index) => {
				const dataTestIdOption = option["data-testid"];
				return (
					<button
						ref={(el) => {
							buttonRefs.current[index] = el;
						}}
						data-testid={dataTestIdOption}
						disabled={option.disabled}
						key={`flat_switch_option_${String(option.value)}`}
						id={`flat_switch_option_${index}`}
						type="button"
						// Single-select toggle: expose the active segment to AT (the active state is otherwise visual-only).
						aria-pressed={isIndexActive(index)}
						className={`${isIndexActive(index) ? "active" : ""} ${option.disabled ? "disabled" : ""}`}
						onClick={() => onClickSwitch(option, index)}
					>
						{/* Invisible bold copy reserves the bold-text width so the button never resizes on toggle */}
						<span aria-hidden="true" style={{ visibility: "hidden", height: 0, display: "block", fontWeight: 600, pointerEvents: "none" }}>
							{option.label}
						</span>
						<span>{option.label}</span>
					</button>
				);
			})}
		</Box>
	);
};
